import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, Fragment as _Fragment, createElementVNode as _createElementVNode, renderSlot as _renderSlot, unref as _unref, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, vModelText as _vModelText, renderList as _renderList, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "input-icon--left" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 0,
  class: "base-input--label"
}
const _hoisted_4 = ["for", "innerHTML"]
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = ["value", "id"]
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = {
  key: 1,
  class: "input-icon--right"
}
const _hoisted_9 = { class: "row h-100 mh-100" }
const _hoisted_10 = { class: "col align-self-center" }
const _hoisted_11 = { class: "input-icon--dropdown" }
const _hoisted_12 = { key: 0 }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = ["innerHTML"]
const _hoisted_15 = ["src"]
const _hoisted_16 = ["innerHTML"]

import {
  reactive,
  computed,
  ref,
  watch,
  nextTick,
} from 'vue';

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCheck, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';


export default /*@__PURE__*/_defineComponent({
  __name: 'SelectField',
  props: {
  modelValue: {},
  valueKey: {
    type: String,
    default: () => 'value',
    required: false,
  },
  search: {
    type: Boolean,
    default: () => false,
  },
  name: {
    type: String,
    required: false,
    default: () => null,
  },
  label: {
    type: String,
    required: false,
    default: () => '&nbsp;',
  },
  options: {
    type: Array,
    required: false,
    default: () => [],
  },
  error: {
    type: [String, Object],
    required: false,
    default: () => '',
  },
  showValidCheck: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  iconLeft: {
    type: [String, Object],
    required: false,
    default: () => null,
  },
  borderless: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  rounded: {
    type: Boolean,
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  hideLabel: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  hideSelectedOption: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  fixSelectedOption: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  image: {
    type: Boolean,
    required: false,
  },
},
  emits: [
  'update:modelValue',
  'input',
],
  setup(__props, { emit: __emit }) {

library.add(faCheck);
library.add(faChevronDown);
library.add(faSearch);

const emit = __emit;

const props = __props;
const root = ref();
const searchInput = ref();

const component = reactive({
  id: `sf-${Math.random()
    .toString(36)
    .substring(2, 11)}`,
  error: props.error || '',
  open: false,
  searchFor: null as string | null,
});

const hasError = computed(() => {
  const compError = `${Array.isArray(component.error) && component.error[0] ? component.error[0] : component.error}`.trim();
  const $error = compError.length ? compError : props.error;

  return $error ? $error.length > 0 : false;
});

const errorMessage = computed(() => {
  if (hasError.value) {
    const compError = `${Array.isArray(component.error) && component.error[0] ? component.error[0] : component.error}`.trim();
    const $error = compError.length ? compError : props.error;

    return Array.isArray($error)
      ? $error[0]
      : $error;
  }

  return '';
});

const value = computed({
  get(): unknown {
    return props.modelValue;
  },
  set(newValue: unknown): void {
    emit('update:modelValue', newValue);
  },
});

const getOptionValue = ($option: string | number | Record<string, unknown>): string | number => {
  if (typeof $option === 'object') {
    return $option[props.valueKey] as string | number;
  }

  return $option;
};

const getOptionLabel = ($option: string | number | Record<string, unknown>) => {
  if (typeof $option === 'object') {
    return $option.label;
  }

  return $option;
};

const getOptionKey = ($option: string | number | Record<string, unknown>) => {
  let $label = $option as string;

  if (typeof $option === 'object') {
    $label = $option.label as string;
  }

  return `${$label}`.replace(/[\W_]+/g, ' ');
};

const selectedLabel = computed(() => {
  let $label = null;

  const $selectedOption = [...props.options].filter(($option) => `${getOptionValue($option as string | Record<string, string>)}` === `${value.value}`);

  if ($selectedOption.length) {
    $label = getOptionLabel($selectedOption[0] as string | Record<string, string>);
  }

  return $label;
});

const setListStyles = () => {
  if (root.value) {
    const rootPosition = root.value.getBoundingClientRect();
    const body = document.querySelector('body');

    // eslint-disable-next-line no-unused-expressions
    body?.style.setProperty('--select-options-list-top', `${rootPosition.bottom}px`);
    // eslint-disable-next-line no-unused-expressions
    body?.style.setProperty('--select-options-list-left', `${rootPosition.left}px`);
    // eslint-disable-next-line no-unused-expressions
    body?.style.setProperty('--select-options-list-width', `${rootPosition.width}px`);
  }

  window.addEventListener('scroll', setListStyles);
};

const removeListStyles = () => {
  const body = document.querySelector('body');

  // eslint-disable-next-line no-unused-expressions
  body?.style.removeProperty('--select-options-list-top');
  // eslint-disable-next-line no-unused-expressions
  body?.style.removeProperty('--select-options-list-left');
  // eslint-disable-next-line no-unused-expressions
  body?.style.removeProperty('--select-options-list-width');

  window.removeEventListener('scroll', setListStyles);
};

const openSelect = () => {
  if (props.options.length > 1) {
    setListStyles();
    nextTick(() => {
      component.open = true;
    });
  }
};

const setValue = ($option: string | Record<string, string>) => {
  value.value = getOptionValue($option);
  component.open = false;
  removeListStyles();
  component.searchFor = null;
};

const ifClickedOutside = (event: MouseEvent) => {
  if (root.value) {
    const withinRootBoundaries = event.composedPath().includes(root.value);
    const withinSearchBoundaries = searchInput.value ? event.composedPath().includes(searchInput.value) : false;

    const withinBoundaries = withinRootBoundaries || withinSearchBoundaries;
    if (!withinBoundaries) {
      component.open = false;
      removeListStyles();
      component.searchFor = null;
    }
  }
};

watch(() => component.open, ($isOpen) => {
  if ($isOpen) {
    document.addEventListener('click', ifClickedOutside);
    setTimeout(() => {
      if (searchInput.value) {
        searchInput.value.focus();
      }
    }, 50);
  } else {
    document.removeEventListener('click', ifClickedOutside);
  }
});

const searchedOptions = computed(() => {
  if (component.searchFor !== null) {
    return [...props.options].filter(($option) => {
      const $optionVal = getOptionValue($option as string | Record<string, string>);
      const $optionLabel = getOptionLabel($option as string | Record<string, string>);

      const valueIncludesSearch = `${$optionVal}`.toLowerCase()
        .includes((component.searchFor || '')
          .toLowerCase());
      const labelIncludesSearch = `${$optionLabel}`.toLowerCase()
        .includes((component.searchFor || '')
          .toLowerCase());
      return valueIncludesSearch || labelIncludesSearch;
    });
  }

  return [...props.options];
});

const filled = computed(() => (props.options || []).filter((option) => (typeof option === 'object' ? (option as Record<string, any>)[props.valueKey] : option) === value.value).length);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["select-field-wrapper fs-regular", {
        open: component.open,
        'hide-selected': __props.hideSelectedOption,
        'fix-selected': __props.fixSelectedOption,
      }]),
    ref_key: "root",
    ref: root
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["base-input-field base-input-field--select", {
          invalid: hasError.value,
          valid: !hasError.value && __props.showValidCheck,
          borderless: __props.borderless,
          rounded: __props.rounded,
          filled: filled.value,
          focused: component.open,
          'icon-left': __props.iconLeft,
          'label-hidden': __props.hideLabel,
        }]),
      onClick: openSelect
    }, [
      (__props.iconLeft)
        ? (_openBlock(), _createElementBlock("div", { key: __props.iconLeft }, [
            _createElementVNode("span", _hoisted_1, [
              (typeof __props.iconLeft === 'object')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    (__props.iconLeft.icon)
                      ? (_openBlock(), _createElementBlock("i", {
                          key: 0,
                          class: _normalizeClass(["fa-fw icon", __props.iconLeft.icon])
                        }, null, 2))
                      : (__props.iconLeft.svg)
                        ? (_openBlock(), _createElementBlock("span", {
                            key: 1,
                            class: "icon-svg",
                            innerHTML: __props.iconLeft.svg
                          }, null, 8, _hoisted_2))
                        : (__props.iconLeft.image)
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 2,
                              class: "image",
                              style: _normalizeStyle({ backgroundImage: `url(${__props.iconLeft.image.url || __props.iconLeft.image})` })
                            }, null, 4))
                          : _createCommentVNode("", true)
                  ], 64))
                : _createCommentVNode("", true),
              (typeof __props.iconLeft === 'string')
                ? (_openBlock(), _createElementBlock("i", {
                    key: 1,
                    class: _normalizeClass(["fa-fw icon", __props.iconLeft])
                  }, null, 2))
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: _normalizeClass(["col-main", { nobrd: ! __props.showValidCheck && __props.options.length === 1 }])
      }, [
        _createElementVNode("div", null, [
          (!__props.hideLabel)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("label", {
                  for: component.id,
                  innerHTML: __props.label
                }, null, 8, _hoisted_4),
                _renderSlot(_ctx.$slots, "tooltip")
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            class: _normalizeClass(["input-label text-start", __props.disabled ? 'disabled' : null]),
            innerHTML: selectedLabel.value || ' '
          }, null, 10, _hoisted_5),
          _createElementVNode("input", {
            class: "base-input__input",
            type: "text",
            readonly: "",
            value: selectedLabel.value,
            id: component.id,
            onFocusin: openSelect
          }, null, 40, _hoisted_6),
          (hasError.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: "base-input-field__error-message fs-small",
                innerHTML: errorMessage.value
              }, null, 8, _hoisted_7))
            : _createCommentVNode("", true)
        ])
      ], 2),
      (!hasError.value && __props.showValidCheck)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _createVNode(_unref(FontAwesomeIcon), { icon: "fa-light fa-check" })
              ])
            ])
          ]))
        : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("div", null, [
        _createElementVNode("span", _hoisted_11, [
          _createVNode(_unref(FontAwesomeIcon), { icon: "fa-light fa-chevron-down" })
        ])
      ], 512), [
        [_vShow, __props.options.length > 1]
      ])
    ], 2),
    (component.open)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 0,
          to: "body"
        }, [
          _createElementVNode("div", {
            class: _normalizeClass(["select-field-list font-regular", { 'search': __props.search }])
          }, [
            (__props.search)
              ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                  _withDirectives(_createElementVNode("input", {
                    type: "text",
                    ref_key: "searchInput",
                    ref: searchInput,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((component.searchFor) = $event))
                  }, null, 512), [
                    [_vModelText, component.searchFor]
                  ]),
                  _createVNode(_unref(FontAwesomeIcon), { icon: "fa-regular fa-search" })
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("ul", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(searchedOptions.value, (option) => {
                return (_openBlock(), _createElementBlock("li", {
                  onClick: ($event: any) => (setValue(option)),
                  key: getOptionKey(option),
                  class: _normalizeClass({'selected': getOptionValue(option) === value.value })
                }, [
                  (typeof option === 'object')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        (option.icon)
                          ? (_openBlock(), _createElementBlock("i", {
                              key: 0,
                              class: _normalizeClass(["fa-fw icon", option.icon])
                            }, null, 2))
                          : (option.svg)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 1,
                                class: "icon-svg",
                                innerHTML: option.svg
                              }, null, 8, _hoisted_14))
                            : (__props.image)
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 2,
                                  class: "image__img",
                                  src: option.image
                                }, null, 8, _hoisted_15))
                              : (option.image)
                                ? (_openBlock(), _createElementBlock("span", {
                                    key: 3,
                                    class: "image",
                                    style: _normalizeStyle({ backgroundImage: `url(${option.image.url || option.image})` })
                                  }, null, 4))
                                : _createCommentVNode("", true)
                      ], 64))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", {
                    innerHTML: getOptionLabel(option)
                  }, null, 8, _hoisted_16)
                ], 10, _hoisted_13))
              }), 128))
            ])
          ], 2)
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})